import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, Shield, Cpu, Activity, ArrowRight, Layers, Terminal, Search, Code, Globe, Box, MousePointer2 } from 'lucide-react';
import { TracePreview } from '../components/TracePreview';

export const Landing: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden selection:bg-emerald-500/30">
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 px-8 flex flex-col items-center text-center overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent -z-10" />
        
        <motion.div
          style={{ opacity, scale }}
          className="max-w-5xl mx-auto"
        >
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-1.5 rounded-full mb-8"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/70">Next-Gen EVM Debugging</span>
          </motion.div>
          
          <h1 className="text-4xl sm:text-6xl md:text-[8rem] font-bold tracking-tighter mb-8 uppercase leading-[0.85] text-white">
            Debug the <span className="text-emerald-500">Unseen.</span>
          </h1>
          
          <p className="text-white/50 max-w-2xl mx-auto text-lg md:text-xl font-medium mb-12 leading-relaxed">
            The most advanced transaction tracing engine for Ethereum and L2s. 
            Visualize execution paths, inspect state changes, and debug with surgical precision.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/app"
              className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-10 py-5 rounded-xl transition-all flex items-center justify-center gap-3 text-sm uppercase tracking-widest group shadow-[0_0_40px_rgba(16,185,129,0.2)]"
            >
              Start Tracing
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/docs"
              className="w-full sm:w-auto bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold px-10 py-5 rounded-xl transition-all text-sm uppercase tracking-widest backdrop-blur-md"
            >
              Read Docs
            </Link>
          </div>
        </motion.div>

        {/* Trace Snippet Shot */}
        <TracePreview />
      </section>

      {/* Marquee Section */}
      <div className="py-8 border-y border-white/5 bg-black overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="flex items-center gap-16 px-8">
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-white/20">Ethereum</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-emerald-500/40">Arbitrum</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-white/20">Optimism</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-emerald-500/40">Polygon</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-white/20">Base</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-emerald-500/40">Linea</span>
            </div>
          ))}
        </div>
      </div>

      {/* Features Grid */}
      <section className="py-32 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-6">Engineered for <span className="text-emerald-500">Precision.</span></h2>
          <p className="text-white/40 max-w-xl mx-auto font-medium">Every instruction, every state change, every call—visualized in a way that makes sense.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { 
              icon: <Terminal className="w-6 h-6 text-emerald-500" />, 
              title: "Opcode Stepper", 
              desc: "Step through execution one instruction at a time. Inspect stack, memory, and storage context at any point." 
            },
            { 
              icon: <Layers className="w-6 h-6 text-blue-500" />, 
              title: "Hierarchical Call Tree", 
              desc: "Visualize complex nested contract calls. Navigate through internal calls and delegate calls with ease." 
            },
            { 
              icon: <Shield className="w-6 h-6 text-orange-500" />, 
              title: "State Diffing", 
              desc: "Instantly see what changed in storage. Decoded slot mapping makes state changes human-readable." 
            },
            { 
              icon: <Zap className="w-6 h-6 text-yellow-500" />, 
              title: "Low Latency", 
              desc: "Optimized retrieval engine ensures traces are fetched and parsed in milliseconds, not seconds." 
            },
            { 
              icon: <Globe className="w-6 h-6 text-purple-500" />, 
              title: "Multi-Chain", 
              desc: "Native support for all major EVM chains and L2s. Debug across the entire ecosystem from one interface." 
            },
            { 
              icon: <Search className="w-6 h-6 text-red-500" />, 
              title: "Deep Search", 
              desc: "Search through trace logs for specific opcodes, addresses, or values. Find exactly what you're looking for." 
            }
          ].map((feature, i) => (
            <motion.div 
              key={i}
              whileHover={{ y: -10 }}
              className="p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:bg-white/[0.04] transition-all hover:border-emerald-500/30 group"
            >
              <div className="mb-6 p-4 bg-white/5 rounded-2xl w-fit group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-4 uppercase tracking-tight">{feature.title}</h3>
              <p className="text-white/40 text-sm leading-relaxed font-medium">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-40 px-8 text-center relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-emerald-500/5 blur-[120px] rounded-full scale-150 -translate-y-1/2" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-8xl font-bold tracking-tighter mb-12 uppercase">
            Stop guessing. <br />Start <span className="text-emerald-500">Tracing.</span>
          </h2>
          <Link
            to="/app"
            className="inline-flex items-center gap-4 bg-white text-black font-bold px-12 py-6 rounded-2xl transition-all hover:bg-emerald-500 hover:scale-105 text-sm uppercase tracking-widest group"
          >
            Launch Debugger
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 border-t border-white/5 px-8 bg-black">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Zap className="text-black w-4 h-4" />
              </div>
              <span className="font-bold text-xl tracking-tighter uppercase">Oru Trace</span>
            </div>
            <p className="text-white/30 max-w-sm text-sm font-medium">
              The professional standard for EVM transaction debugging and execution analysis.
            </p>
          </div>
          <div className="space-y-4">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/20">Product</h4>
            <ul className="space-y-2 text-sm text-white/50 font-medium">
              <li><Link to="/features" className="hover:text-emerald-500 transition-colors">Features</Link></li>
              <li><Link to="/docs" className="hover:text-emerald-500 transition-colors">Documentation</Link></li>
              <li><Link to="/app" className="hover:text-emerald-500 transition-colors">Debugger</Link></li>
            </ul>
          </div>
          <div className="space-y-4">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/20">Connect</h4>
            <ul className="space-y-2 text-sm text-white/50 font-medium">
              <li><a href="#" className="hover:text-emerald-500 transition-colors">Twitter</a></li>
              <li><a href="#" className="hover:text-emerald-500 transition-colors">GitHub</a></li>
              <li><a href="#" className="hover:text-emerald-500 transition-colors">Discord</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-white/20">
          <span>© 2026 ORU LABS. ALL RIGHTS RESERVED.</span>
          <div className="flex gap-8">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
      `}</style>
    </div>
  );
};
